import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { XIcon, InfoIcon } from 'lucide-react';
interface PartnerUserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
  preselectedPartner: string | null;
}
interface FormData {
  name: string;
  fatherName: string;
  idNumber: string;
  email: string;
  phone: string;
  username: string;
  partner: string;
  userType: 'maker' | 'checker';
}
interface FormErrors {
  name?: string;
  fatherName?: string;
  idNumber?: string;
  email?: string;
  phone?: string;
  username?: string;
  partner?: string;
  userType?: string;
}
const PARTNERS = ['Al Ansari Exchange', 'Al Fardan Exchange', 'UAE Exchange', 'Lulu Exchange', 'Xpress Money'];
const PartnerUserFormDrawer: React.FC<PartnerUserFormProps> = ({
  open,
  user,
  onClose,
  onSave,
  preselectedPartner
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    fatherName: '',
    idNumber: '',
    email: '',
    phone: '',
    username: '',
    partner: '',
    userType: 'maker'
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        fatherName: user.fatherName || '',
        idNumber: user.idNumber || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        partner: user.partner || '',
        userType: user.userType || 'maker'
      });
    } else {
      setFormData({
        name: '',
        fatherName: '',
        idNumber: '',
        email: '',
        phone: '',
        username: '',
        partner: preselectedPartner || '',
        userType: 'maker'
      });
    }
    setErrors({});
  }, [user, open, preselectedPartner]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+92\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must start with +92 and be exactly 10 digits after +92';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.partner) {
      newErrors.partner = 'Partner is required';
    }
    if (!formData.userType) {
      newErrors.userType = 'User Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: user?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-1712" data-mp="3gbvntdftxcom">
      <Box sx={{
      p: 3
    }} data-id="element-1713" data-mp="3daitzyllca02">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-1714" data-mp="3dc3mliochiva">
          <Typography variant="h6" data-id="element-1715" data-mp="33imwaonf2lwd">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1716" data-mp="2zgrtffpu80gh">
            <XIcon size={18} data-id="element-1717" data-mp="2uksy1863di6k" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-1718" data-mp="2pky8b7y1yyn3">
          <TextField fullWidth margin="normal" label="Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} required data-id="element-1719" data-mp="2km3rucnk5m65" />
          <TextField fullWidth margin="normal" label="Father Name" name="fatherName" value={formData.fatherName} onChange={handleChange} error={!!errors.fatherName} helperText={errors.fatherName} data-id="element-1720" data-mp="etycir0zf3c8" />
          <TextField fullWidth margin="normal" label="ID Number (Optional)" name="idNumber" value={formData.idNumber} onChange={handleChange} error={!!errors.idNumber} helperText={errors.idNumber} data-id="element-1721" data-mp="26glbkz4fdmys" />
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-1722" data-mp="1ir0p4692c7kr" />
          <TextField fullWidth margin="normal" label="Phone" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone || 'Must start with +92 and be 10 digits after +92'} required data-id="element-1723" data-mp="2y9lq0b0wwej6" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-1724" data-mp="1d1hbd6zyxqcd" />
          <FormControl fullWidth margin="normal" error={!!errors.partner} required data-id="element-1725" data-mp="u4za9yz8ce3g">
            <InputLabel id="partner-label" data-id="element-1726" data-mp="1n3q89wlnp01">Partner</InputLabel>
            <Select labelId="partner-label" name="partner" value={formData.partner} onChange={handleChange} label="Partner" data-id="element-1727" data-mp="1l94itdi7suw">
              {PARTNERS.map(partner => <MenuItem key={partner} value={partner} data-id="element-1728" data-mp="2d2m5krixii5w">
                  {partner}
                </MenuItem>)}
            </Select>
            {errors.partner && <FormHelperText data-id="element-1729" data-mp="23c9cuc0ynioo">{errors.partner}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth margin="normal" error={!!errors.userType} required data-id="element-1730" data-mp="2r1u3zi79pm91">
            <InputLabel id="user-type-label" data-id="element-1731" data-mp="2vzndak3vawdj">User Type</InputLabel>
            <Select labelId="user-type-label" name="userType" value={formData.userType} onChange={handleChange} label="User Type" endAdornment={<Tooltip title="Select user's operational role (Maker creates, Checker reviews/approves)." placement="top" arrow data-id="element-1733" data-mp="16l7fcf71srst">
                  <IconButton size="small" sx={{
              mr: 0.5,
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }} onClick={e => e.stopPropagation()} data-id="element-1734" data-mp="22byfws9zz3nr">
                    <InfoIcon size={16} data-id="element-1735" data-mp="2lxuqwrdlx3b9" />
                  </IconButton>
                </Tooltip>} data-id="element-1732" data-mp="2i1oy5chq2vwz">
              <MenuItem value="maker" data-id="element-1736" data-mp="2h25x74o9qgmv">Maker</MenuItem>
              <MenuItem value="checker" data-id="element-1737" data-mp="2c8kz7cmodmno">Checker</MenuItem>
            </Select>
            <FormHelperText data-id="element-1738" data-mp="27ajb2dae6x5c">
              {errors.userType || "Select user's operational role (Maker creates, Checker reviews/approves)."}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1739" data-mp="2zy5g7pz9q9ub">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1740" data-mp="3c9y2edyn0zn4">
          <Button variant="outlined" onClick={onClose} data-id="element-1741" data-mp="3ham2rq8rasu0">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-1742" data-mp="3m4zrxawts3q8">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default PartnerUserFormDrawer;